import { useMemo, useState } from 'react';

import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import type { SubscriptionStatusIntent } from '../../../components/reusable/users-list/user-big-item/useUserBigItem';
import { useLazyLoad } from '../../../hooks/use-lazy-load';
import { useLazyGetUserPosts } from '../../../redux/api/postsApi';
import {
  useChangeUserSubscriptionStatus,
  useGetMyDetailsDetailed,
  useGetUserDetails,
  useSubscribeToUser,
  useUnsubscribeFromUser,
} from '../../../redux/api/users/usersApi';
import { AvatarSize, RelationStatus } from '../../../shared/constants';
import {
  communitiesFromCommunitySubscriptionRelations,
  usersFromSubscriberRelations,
  usersFromSubscriptionRelations,
} from '../../../utils/collections/relation-entities';

const desktopRelationsItemsCount = 5;
const mobileRelationAvatarSize = AvatarSize.s;
const mobileRelationItemPaddingX = 8;
const mobileRelationItemGap = 4;

export function useUserPage(userId: string) {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const { data: user } = useGetMyDetailsDetailed();

  const {
    data: reviewedUser,
    isFetching: userLoading,
    isError: isReviewedUserError,
    error: reviewedUserError,
  } = useGetUserDetails({
    id: userId,
  });
  const reviewedUserSubscriptions = usersFromSubscriptionRelations(
    reviewedUser?.subscriptions,
  );
  const reviewedUserSubscribers = usersFromSubscriberRelations(
    reviewedUser?.subscribers,
  );
  const communities = communitiesFromCommunitySubscriptionRelations(
    reviewedUser?.communitySubscriptions,
  );
  const [changeUserSubscriberStatus, { isLoading: isChangeStatusLoading }] =
    useChangeUserSubscriptionStatus();
  const [statusIntent, setStatusIntent] =
    useState<SubscriptionStatusIntent | null>(null);
  // useNotFound(!userLoading && !reviewedUser);

  const isCurrentUser = user?.id === userId;
  const [subscribeToUser, { isLoading: isSubscribeLoading }] =
    useSubscribeToUser();
  const [unsubscribeFromUser, { isLoading: isUnsubscribeLoading }] =
    useUnsubscribeFromUser();
  const {
    items: posts,
    footerRef,
    refetchAll,
    isFetching: isFetchingPosts,
    error: postsError,
  } = useLazyLoad(
    useLazyGetUserPosts,
    { userId },
    (items) => ({
      timestamp: items?.length
        ? new Date(items[items.length - 1]?.createdAt).valueOf()
        : new Date().valueOf() + 5000,
    }),
    !userId,
  );

  async function onSubscribe() {
    try {
      await subscribeToUser({ id: reviewedUser?.id }).unwrap();
    } catch (e) {
      console.log(e);
    }
  }

  async function onUnsubscribe() {
    try {
      await unsubscribeFromUser({ id: reviewedUser?.id }).unwrap();
    } catch (e) {
      console.log(e);
    }
  }

  async function onSubscriptionStatusChange(
    status: RelationStatus,
    intent: SubscriptionStatusIntent,
  ) {
    setStatusIntent(intent);
    try {
      await changeUserSubscriberStatus({
        id: userId,
        body: { status },
      }).unwrap();
    } catch (e) {
      console.log(e);
    } finally {
      setStatusIntent(null);
    }
  }

  const isRelationPanel =
    reviewedUserSubscriptions?.length ||
    reviewedUserSubscribers?.length ||
    communities?.length;
  const isUserLoading = userLoading && !reviewedUser;
  const showNoResult = !!posts && !posts.length && !isFetchingPosts;
  const showNoDetailsAccess =
    !userLoading && !reviewedUser?.permissions?.canSeeDetails;
  const showNoPostsAccess =
    !userLoading && !reviewedUser?.permissions?.canSeePosts;
  const isSubscriptionRequestSent =
    !!reviewedUser?.permissions?.canCancelRequestSubscription;
  const showPrivateSubscribeAction =
    !!reviewedUser?.permissions?.canSubscribe ||
    !!reviewedUser?.permissions?.canRequestSubscription;
  const privatePostsSubscribeLabelKey =
    reviewedUser?.permissions?.canRequestSubscription &&
    !reviewedUser?.permissions?.canSubscribe
      ? 'common.requestSubscription'
      : 'common.subscribe';
  const noAccessTitleKey = isSubscriptionRequestSent
    ? 'users.privateProfilePendingTitle'
    : 'users.privateProfileRequestTitle';
  const noAccessDescriptionKey = isSubscriptionRequestSent
    ? 'users.privateProfilePendingDescription'
    : 'users.privateProfileRequestDescription';
  const visibleRelationsCount = useMemo(() => {
    if (isMdUp) {
      return desktopRelationsItemsCount;
    }

    const viewportWidth =
      typeof window !== 'undefined' ? window.innerWidth : undefined;
    const mobileRelationItemWidth =
      mobileRelationAvatarSize + mobileRelationItemPaddingX;
    const mobileVisibleRelationsCount = Math.floor(
      ((viewportWidth ?? mobileRelationItemWidth) + mobileRelationItemGap) /
        (mobileRelationItemWidth + mobileRelationItemGap),
    );

    return Math.max(1, mobileVisibleRelationsCount);
  }, [isMdUp]);

  // function onSendMessageClick() {
  //   const chat = _.find(chats, (chat) => chat.idPrivate && _.find(chat.members, m => m.userId === reviewedUser.id)?.userId);
  //
  //   if (!chat) {
  //     const newChat = {
  //       isPrivate: true,
  //       members: [{ userId: reviewedUser.id, }, { userId: user.id, }]
  //     }
  //
  //     addChatApi({ body: newChat }).then(response => {
  //       navigate(`${Paths.chats}/${response.data.id}`);
  //     });
  //   } else {
  //     navigate(`${Paths.chats}/${chat.id}`);
  //   }
  // }

  return {
    isRelationPanel,
    isCurrentUser,
    posts,
    reviewedUser,
    footerRef,
    reviewedUserSubscriptions,
    reviewedUserSubscribers,
    communities,
    userLoading,
    isUserLoading,
    showNoResult,
    showNoDetailsAccess,
    showNoPostsAccess,
    isSubscriptionRequestSent,
    showPrivateSubscribeAction,
    privatePostsSubscribeLabelKey,
    noAccessTitleKey,
    noAccessDescriptionKey,
    visibleRelationsCount,
    isReviewedUserError,
    reviewedUserError,
    onSubscribe,
    onUnsubscribe,
    isSubscribeLoading,
    isUnsubscribeLoading,
    isApproveStatusLoading: statusIntent === 'approve' && isChangeStatusLoading,
    isRejectStatusLoading: statusIntent === 'reject' && isChangeStatusLoading,
    isDropStatusLoading: statusIntent === 'drop' && isChangeStatusLoading,
    onSubscriptionStatusChange,
    refetchAll,
    isFetchingPosts,
    postsError,
  };
}
